import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import api from '@/admin/lib/api'
import type { ApiKey, ApiKeyCreated } from '@/admin/types/api-keys'

import { toastApiError } from '@/shared/lib/api-error'

interface RotateApiKeyDialogProps {
  open: boolean
  onClose: () => void
  apiKey: ApiKey | null
  onRotated: (created: ApiKeyCreated) => void
}

export function RotateApiKeyDialog({
  open,
  onClose,
  apiKey,
  onRotated,
}: RotateApiKeyDialogProps) {
  const [rotating, setRotating] = useState(false)

  async function handleRotate() {
    if (!apiKey) return
    setRotating(true)
    try {
      const res = await api.post(`/auth/api-keys/${apiKey.uuid}/rotate/`)
      toast.success('API key rotated')
      onRotated(res.data as ApiKeyCreated)
      onClose()
    } catch (err) {
      toastApiError(err, 'Failed to rotate API key')
    } finally {
      setRotating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rotate API Key</DialogTitle>
        </DialogHeader>

        <p className="text-[13px] text-[var(--ink-2)]">
          Rotating issues a new secret for{' '}
          <span className="font-medium text-[var(--ink)]">{apiKey?.name}</span> and revokes the
          current one immediately. Update your integration with the new key before rotating - the
          current key stops working right away.
        </p>

        <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleRotate} disabled={rotating}>
            {rotating && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
            Rotate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
